import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { userItemSelectionList } from '../../../../utils/items-selection'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Wrench, Wind, Droplets, ThermometerSun, Sparkles, Gauge, Settings, Waves } from 'lucide-react'

// Icon and color mapping for service items
export const getServiceIconAndColor = (itemName) => {
    const iconMap = {
        "General Servicing": { icon: Sparkles, bgColor: "bg-amber-100", iconColor: "text-amber-500" },
        "Chemical Wash": { icon: Droplets, bgColor: "bg-blue-100", iconColor: "text-blue-500" },
        "Chemical Hydro Wash": { icon: Waves, bgColor: "bg-indigo-100", iconColor: "text-indigo-500" },
        "Chemical Overhaul": { icon: Settings, bgColor: "bg-purple-100", iconColor: "text-purple-500" },
        "Condenser Cleaning": { icon: Wind, bgColor: "bg-cyan-100", iconColor: "text-cyan-500" },
        "Condenser Chem. Wash": { icon: Wind, bgColor: "bg-teal-100", iconColor: "text-teal-500" },
        "Drain Pipe Flush": { icon: Droplets, bgColor: "bg-sky-100", iconColor: "text-sky-500" },
        "Gas Refill Half Tank": { icon: Gauge, bgColor: "bg-green-100", iconColor: "text-green-500" },
        "Gas Refill Full Tank": { icon: Gauge, bgColor: "bg-emerald-100", iconColor: "text-emerald-500" },
        "Aircon Repair (est.)": { icon: Wrench, bgColor: "bg-red-100", iconColor: "text-red-500" }
    };
    return iconMap[itemName] || { icon: ThermometerSun, bgColor: "bg-orange-100", iconColor: "text-orange-500" };
};

// Service Item Button - Compact with colorful icons
const ServiceItemButton = ({ item, onClick }) => {
    const { icon: Icon, bgColor, iconColor } = getServiceIconAndColor(item);
    
    return (
        <motion.button
            type="button"
            className="flex items-center gap-2 p-2.5 bg-white rounded-xl border border-gray-200 hover:border-orange-400 hover:shadow-md transition-all text-left group"
            onClick={() => onClick(item)}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
        >
            <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${bgColor} ${iconColor} shrink-0`}>
                <Icon className="w-4 h-4" />
            </div>
            <span className="text-sm font-medium text-gray-700 group-hover:text-orange-600 transition-colors truncate">
                {item === "Aircon Repair (est.)" ? "Aircon Repair" : item}
            </span>
        </motion.button>
    );
};

export const AddAirconItems = (props) => {
    const addItem = props.addItem
    const setIsShowAddItem = props.setIsShowAddItem
    
    const [itemSelection, setItemSelection] = useState([])
    const [mounted, setMounted] = useState(false)

    useEffect(() => {
        setMounted(true)   
        setItemSelection(userItemSelectionList["Aircon Services"])
    }, [])

    if (!mounted) return null

    return createPortal(
        <AnimatePresence>
            <div className="fixed inset-0 backdrop-blur-sm bg-black/40 flex items-center justify-center z-[60] p-3">
                <motion.div 
                    className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden"
                    initial={{ opacity: 0, scale: 0.95, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: 20 }}
                    transition={{ type: "spring", stiffness: 300, damping: 25 }}
                >
                    {/* Header */}
                    <div className="flex items-center justify-between px-4 py-3 bg-linear-to-r from-orange-500 to-amber-500">
                        <h3 className="text-white font-bold">Select Service</h3>
                        <button 
                            type="button" 
                            onClick={() => setIsShowAddItem(false)} 
                            className="w-8 h-8 flex items-center justify-center text-white/80 hover:text-white hover:bg-white/20 rounded-full transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Service Items Grid - Compact */}
                    <div className="p-3 max-h-[70vh] overflow-y-auto">
                        <div className="grid sm:grid-cols-2 gap-2">
                            {/* Aircon Repair - First */}
                            <ServiceItemButton 
                                item="Aircon Repair (est.)" 
                                onClick={addItem} 
                            />
                            
                            {/* Other Services */}
                            {itemSelection.map((item, index) => (
                                <ServiceItemButton 
                                    key={index} 
                                    item={item} 
                                    onClick={addItem} 
                                />
                            ))}
                        </div>
                    </div>

                    {/* Footer Hint */}
                    <div className="px-4 py-2 bg-gray-50 border-t border-gray-100">
                        <p className="text-2xs text-gray-400 text-center">
                            Tap to add service • Adjust quantities after
                        </p>
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>,
        document.body
    )
}
